import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { BadgeCheck } from "lucide-react";
import { useMutation, useQuery } from "@apollo/client/react";
import { ContentSharingPolicyDocument, UpdateSystemSettingsDocument } from "@/graphql/generated/operations";
import { Switch } from "@/components/design/Switch";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Disclosure } from "@/components/design/Disclosure";
import { groupLabel, VERIFIED_GROUP_ID } from "./access-group-labels";
import { usePendingScope } from "@/lib/pending-edits";
import { LocalizedError } from "@/lib/localized-error";
import { userErrorMessage } from "@/lib/user-error";

export function ContentSharingPolicy() {
  const intl = useIntl();
  const query = useQuery(ContentSharingPolicyDocument, { fetchPolicy: "network-only" });
  const [change] = useMutation(UpdateSystemSettingsDocument);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const [draft, setDraft] = useState<boolean | null>(null);
  const stored = query.data?.systemSettings?.sharedItemsRequireDefaultRecipients ?? true;
  const checked = draft ?? stored;
  const dirty = !!query.data && checked !== stored;
  usePendingScope("content-sharing-policy", { dirty, save, discard: () => { setDraft(null); setError(null); } });

  function save(): Promise<void> {
    if (inFlight.current) return inFlight.current;
    const pending = update(checked).finally(() => { inFlight.current = null; });
    inFlight.current = pending;
    return pending;
  }
  const label = intl.formatMessage({ id: "admin.groups.requireDefaultRecipients" });

  async function update(required: boolean) {
    if (!dirty) return;
    setSaving(true);
    try {
      await change({ variables: { input: { data: { sharedItemsRequireDefaultRecipients: required } } } });
      await query.refetch();
      setDraft(null);
      setError(null);
    } catch (cause) {
      const message = userErrorMessage(intl, cause);
      setError(message);
      throw new LocalizedError(message);
    } finally { setSaving(false); }
  }

  return <section className="box bg-surface p-5" aria-label={intl.formatMessage({ id: "admin.groups.sharingPolicyTitle" })}>
    <BoxHeader title={intl.formatMessage({ id: "admin.groups.sharingPolicyTitle" })} />
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <label htmlFor="content-sharing-policy" className="text-smycles-body text-ink">{label}</label>
        <Switch id="content-sharing-policy" checked={checked}
          disabled={saving || query.loading || !query.data || !!query.error}
          onChange={setDraft} ariaLabel={label} />
      </div>
      <div className="space-y-2 text-smycles-meta text-ink">
        {query.data && <p className="text-ink">{intl.formatMessage({ id: "admin.groups.defaultRecipientGroups" }, {
          groups: intl.formatList(query.data.defaultContentSharingRecipients.map((group) =>
            <strong key={group.id} className="inline-flex items-center gap-1 align-middle font-semibold text-ink">
              {group.id === VERIFIED_GROUP_ID && <BadgeCheck className="size-4 shrink-0" aria-hidden="true" />}
              {groupLabel(intl, group)}
            </strong>))
            || intl.formatMessage({ id: "admin.nonePlaceholder" }),
        })}</p>}
        <p>{intl.formatMessage({ id: "admin.groups.sharingPolicyGlobal" })}</p>
      </div>
      <Disclosure title={intl.formatMessage({ id: "admin.groups.sharingPolicyDetails" })}>
        <div className="max-w-3xl space-y-2 text-smycles-body text-ink">
          <p className={checked ? "font-semibold text-ink" : undefined}>{intl.formatMessage({ id: "admin.groups.requireDefaultRecipientsOn" })}</p>
          <p className={!checked ? "font-semibold text-ink" : undefined}>{intl.formatMessage({ id: "admin.groups.requireDefaultRecipientsOff" })}</p>
          <p>{intl.formatMessage({ id: "admin.groups.requireDefaultRecipientsHint" })}</p>
        </div>
      </Disclosure>
    </div>
    {(error || query.error) && <p role="alert" className="text-smycles-body text-ink">
      {error ?? userErrorMessage(intl, query.error)}
    </p>}
  </section>;
}
